import { Component, inject, signal, computed, OnDestroy, OnInit } from '@angular/core';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RatingModule } from '@idb/designsystem';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { TextFieldModule } from '@angular/cdk/text-field';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeedbackService } from '../../../services/feedback.service';
import { NEGATIVE_OPTIONS, NEUTRAL_OPTIONS, POSITIVE_OPTIONS } from '../../../constants/feedback-options';

type Step = 'rating' | 'options' | 'thanks';

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    RatingModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatInputModule,
    TextFieldModule,
    TranslateModule,
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
})
export class FeedbackComponent implements OnInit, OnDestroy {
  dialogRef = inject(DialogRef);
  data = inject(DIALOG_DATA);
  fb = inject(FormBuilder);
  translateService = inject(TranslateService);
  feedbackService = inject(FeedbackService);
  rating = signal(0);
  otherSelected = signal(false);
  step = signal<Step>('rating');
  checkedOptions = signal<Record<string, boolean>>({});

  form = this.fb.group({
    other: ['', [
      Validators.maxLength(1000),
      FeedbackComponent.noInjection
    ]],
  });

  private otherValue = toSignal(
    this.form.get('other')!.valueChanges.pipe(map(v => v?.trim() ?? '')),
    { initialValue: '' }
  );

  isPositive = computed(() => this.rating() >= 4);
  isNeutral = computed(() => this.rating() === 3);
  isNegative = computed(() => this.rating() <= 2);

  currentOptions = computed(() => {
    if (this.isPositive()) return POSITIVE_OPTIONS;
    if (this.isNeutral()) return NEUTRAL_OPTIONS;
    return NEGATIVE_OPTIONS;
  });

  currentQuestion = computed(() => {
    if (this.isPositive()) return 'RATING_POP_UP.POSITIVE.QUESTION';
    if (this.isNeutral()) return 'RATING_POP_UP.NEUTRAL.QUESTION';
    return 'RATING_POP_UP.NEGATIVE.QUESTION';
  });


  canContinue = computed(() => {
    if (this.step() === 'rating') return this.rating() > 0;
    if (this.step() === 'options') {
      const otherIsSelected = this.otherSelected();
      const textareaFilled = this.otherValue().length > 0;
      if (otherIsSelected && !textareaFilled) return false;
      if (this.form.get('other')!.invalid) return false;
      return true;
    }
    return true;
  });

  private _thanksTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit() {
    this.form.get('other')!.setValue('');
  }

  onRatingChange(value: number) {
    this.rating.set(value);
    if (this.step() !== 'options') {
      this.step.set('rating');
    }
    this.checkedOptions.set({});
    this.form.get('other')!.setValue('');
    this.otherSelected.set(value <= 3);
  }

  continue() {
    if (this.step() === 'rating') {
      this.step.set('options');
      return;
    }
    if (this.step() === 'options') {
      this._saveFeedback();
      this._goToThanks();
    }
  }

  onOptionChange(option: string, checked: boolean) {
    this.checkedOptions.update(current => ({ ...current, [option]: checked }));
  }

  onOtherChange(event: { checked: boolean }) {
    this.otherSelected.set(event.checked);
    if (!event.checked) {
      this.form.get('other')!.setValue('');
    }
  }

  private _goToThanks() {
    this.step.set('thanks');
    this._thanksTimer = setTimeout(() => {
      this.dialogRef.close();
      this.feedbackService._showCsat = false;
    }, 3000);
  }

  private _saveFeedback() {
    const selectedOptions = this.currentOptions().filter(opt => this.checkedOptions()[opt]);
    const translatedOptions = selectedOptions.map(opt =>
      this.translateService.instant(opt)
    );
    const otherText = this.otherSelected() ? (this.form.get('other')!.value?.trim() ?? '') : '';
    this.feedbackService.postFeedback(this.rating(), otherText || undefined, translatedOptions).subscribe();
  }

  private static noInjection(control: AbstractControl): ValidationErrors | null {
    const value = control.value ?? '';
    const dangerousPattern = /<[^>]*>|javascript:|on\w+\s*=|<script|<\/script|<!--/i;
    return dangerousPattern.test(value) ? { injection: true } : null;
  }

  ngOnDestroy() {
    if (this._thanksTimer) clearTimeout(this._thanksTimer);
  }
}
